import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import './Carousel.css';

/**
 * Lightweight Carousel component to replace Antd Carousel
 * Reduces bundle size by ~400-500KB by removing antd dependency
 * 
 * API compatible with Antd Carousel:
 * - ref: Use ref.current.goTo(index), ref.current.next(), ref.current.prev()
 * - afterChange: Callback when slide changes
 * - fade: Enable fade transition
 * - speed: Transition duration in ms
 * - style: Custom styles
 */
const Carousel = forwardRef(({ 
  children, 
  afterChange, 
  fade = false, 
  speed = 300,
  style = {},
  className = ''
}, ref) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const items = React.Children.toArray(children);
  const totalItems = items.length;

  // Expose methods via ref (compatible with Antd Carousel API)
  useImperativeHandle(ref, () => ({
    goTo: (index) => {
      if (index >= 0 && index < totalItems) {
        setCurrentIndex(index);
      }
    },
    next: () => {
      setCurrentIndex((prev) => (prev + 1) % totalItems);
    },
    prev: () => {
      setCurrentIndex((prev) => (prev - 1 + totalItems) % totalItems);
    },
    getCurrentIndex: () => currentIndex
  }));

  // Call afterChange callback when index changes
  useEffect(() => {
    if (afterChange && typeof afterChange === 'function') {
      afterChange(currentIndex);
    }
  }, [currentIndex, afterChange]);

  const transitionStyle = {
    transition: `opacity ${speed}ms ease-in-out, transform ${speed}ms ease-in-out`
  };

  return (
    <div 
      className={`custom-carousel ${className}`}
      style={{ ...style, ...transitionStyle }}
    >
      <div className="carousel-container">
        {items.map((item, index) => {
          const isActive = index === currentIndex;
          
          if (fade) {
            // Fade transition
            return (
              <div
                key={index}
                className={`carousel-item ${isActive ? 'active' : ''}`}
                style={{
                  opacity: isActive ? 1 : 0,
                  position: isActive ? 'relative' : 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  zIndex: isActive ? 1 : 0,
                  ...transitionStyle
                }}
              >
                {item}
              </div>
            );
          } else {
            // Slide transition
            return (
              <div
                key={index}
                className={`carousel-item ${isActive ? 'active' : ''}`}
                style={{
                  transform: `translateX(${(index - currentIndex) * 100}%)`,
                  ...transitionStyle
                }}
              >
                {item}
              </div>
            );
          }
        })}
      </div>
      
      {/* Dots indicator */}
      {totalItems > 1 && (
        <div className="carousel-dots">
          {items.map((_, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
});

Carousel.displayName = 'Carousel';

export default Carousel;
